import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Layout from '../../components/common/pageLayout';
import {
    v4_crew_mailbox_activity,
    v4_crew_mailbox_configuration,
    v4_crew_mailbox_configure,
    v4_crew_mailbox_remove,
} from '../../actions/v4/mailBox';

// A crew mailbox belongs to the ship, not to a person. Admin sets the shared
// credential here; who may read it follows the ship's current Master / Chief
// Engineer, which is set on the ship record itself.
const blankForm = {
    shipId: '',
    host: 'gmail',
    email: '',
    password: '',
    imap_host: '',
    imap_port: '',
    imap_tls: 'true',
    smtp_host: '',
    smtp_port: '',
    smtp_secure: 'true',
};

const Index = () => {
    const [showLoader, setShowLoader] = useState(false);
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState(blankForm);
    const [activity, setActivity] = useState(null);

    const load = () => {
        setShowLoader(true);
        return v4_crew_mailbox_configuration().then((res) => {
            if (res.status) setRows(res.result || []);
            else toast.error(res.error || 'Could not load crew mailboxes');
            setShowLoader(false);
        });
    };

    useEffect(() => { load(); }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.shipId || !form.email || !form.password) {
            toast.error('Ship, email and password are required');
            return;
        }
        setShowLoader(true);
        v4_crew_mailbox_configure(form).then((res) => {
            if (res.status) {
                toast.success(res.result.message || 'Crew mailbox saved');
                setForm(blankForm);
                load();
            } else {
                toast.error(res.error || 'Could not save crew mailbox');
                setShowLoader(false);
            }
        });
    };

    const onRemove = (row) => {
        // Removing the credential cuts the ship off from its own correspondence
        // history, so make the admin name the ship before it happens.
        const typed = window.prompt(
            `Remove the crew mailbox for ${row.SHIP_NAME}? The Master and Chief Engineer lose access to ${row.EMAIL} immediately. Type the ship name to confirm.`
        );
        if (typed !== row.SHIP_NAME) return;

        setShowLoader(true);
        v4_crew_mailbox_remove(row.SHIP_ID).then((res) => {
            if (res.status) {
                toast.success(res.result.message || 'Crew mailbox removed');
                load();
            } else {
                toast.error(res.error || 'Could not remove crew mailbox');
                setShowLoader(false);
            }
        });
    };

    const onShowActivity = (row) => {
        setActivity({ ship: row, rows: null });
        v4_crew_mailbox_activity(row.SHIP_ID).then((res) => {
            if (res.status) setActivity({ ship: row, rows: res.result || [] });
            else {
                toast.error(res.error || 'Could not load activity');
                setActivity(null);
            }
        });
    };

    const isOtherHost = form.host === 'other';
    const shipsWithoutMailbox = rows.filter((r) => !r.MAILBOX_ID);

    return (
        <Layout title="Crew Mailbox">
            <div className="alert alert-info">
                One shared mailbox per ship, passed from crew to crew. Only the ship's
                current Master and Chief Engineer can open it &mdash; change those on the
                ship record to hand it over. Mail sent from it signs as the post and the
                ship, never as a person.
            </div>

            <form onSubmit={handleSubmit} className="mb-4">
                <div className="row">
                    <div className="col-md-4 mb-3">
                        <label>Ship <span className="text-danger">*</span></label>
                        <select name="shipId" value={form.shipId} onChange={handleChange} className="form-control">
                            <option value="">-- Select Ship --</option>
                            {rows.map((r) => (
                                <option key={r.SHIP_ID} value={r.SHIP_ID}>
                                    {r.SHIP_NAME}{r.MAILBOX_ID ? ' (replace existing)' : ''}
                                </option>
                            ))}
                        </select>
                        {shipsWithoutMailbox.length === 0 && rows.length > 0 && (
                            <small className="text-muted">Every ship already has a mailbox; selecting one rotates its credential.</small>
                        )}
                    </div>

                    <div className="col-md-4 mb-3">
                        <label>Host</label>
                        <select name="host" value={form.host} onChange={handleChange} className="form-control">
                            <option value="gmail">Gmail</option>
                            <option value="yahoo">Yahoo</option>
                            <option value="hotmail">Hotmail</option>
                            <option value="other">Other</option>
                        </select>
                        <small className="text-muted">
                            Microsoft SSO is not offered here: it signs in one named person, not a shared post.
                        </small>
                    </div>

                    <div className="col-md-4 mb-3">
                        <label>Email <span className="text-danger">*</span></label>
                        <input name="email" value={form.email} onChange={handleChange} className="form-control" />
                    </div>

                    <div className="col-md-4 mb-3">
                        <label>Password <span className="text-danger">*</span></label>
                        <input type="password" name="password" value={form.password} onChange={handleChange} className="form-control" />
                        <small className="text-muted">Never shown again once saved.</small>
                    </div>

                    {isOtherHost && (
                        <>
                            <div className="col-md-4 mb-3">
                                <label>IMAP Host</label>
                                <input name="imap_host" value={form.imap_host} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label>IMAP Port</label>
                                <input name="imap_port" value={form.imap_port} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label>IMAP TLS</label>
                                <select name="imap_tls" value={form.imap_tls} onChange={handleChange} className="form-control">
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>
                            <div className="col-md-4 mb-3">
                                <label>SMTP Host</label>
                                <input name="smtp_host" value={form.smtp_host} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label>SMTP Port</label>
                                <input name="smtp_port" value={form.smtp_port} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label>SMTP Secure</label>
                                <select name="smtp_secure" value={form.smtp_secure} onChange={handleChange} className="form-control">
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>
                        </>
                    )}
                </div>

                <button type="submit" className="btn btn-primary" disabled={showLoader}>
                    Save Crew Mailbox
                </button>
            </form>

            <div className="table-responsive">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Ship</th>
                            <th>Crew Mailbox</th>
                            <th>Master</th>
                            <th>Chief Engineer</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.SHIP_ID}>
                                <td>{r.SHIP_NAME}{r.HULL_NUMBER ? ` (${r.HULL_NUMBER})` : ''}</td>
                                <td>{r.EMAIL || <span className="text-muted">Not configured</span>}</td>
                                <td>{r.MASTER_NAME || <span className="text-danger">Not assigned</span>}</td>
                                <td>{r.CHIEF_ENGINEER_NAME || <span className="text-danger">Not assigned</span>}</td>
                                <td>
                                    {r.MAILBOX_ID && (
                                        <>
                                            <button type="button" className="btn btn-sm btn-link" onClick={() => onShowActivity(r)}>
                                                Activity
                                            </button>
                                            <button type="button" className="btn btn-sm btn-link text-danger" onClick={() => onRemove(r)}>
                                                Remove
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && !showLoader && (
                            <tr><td colSpan={5} className="text-center text-muted">No ships</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {activity && (
                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">Activity &mdash; {activity.ship.SHIP_NAME}</h5>
                        <button type="button" className="btn btn-sm btn-link" onClick={() => setActivity(null)}>Close</button>
                    </div>
                    <p className="text-muted">
                        Outgoing crew mail carries no personal name, so this is the only record of who acted.
                    </p>
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Who</th>
                                    <th>Acting as</th>
                                    <th>Action</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activity.rows === null && (
                                    <tr><td colSpan={5} className="text-center text-muted">Loading…</td></tr>
                                )}
                                {activity.rows?.map((a) => (
                                    <tr key={a.ID}>
                                        <td>{a.CREATED_ON}</td>
                                        <td>{a.ACTING_USER_NAME || '—'}</td>
                                        <td>{a.ACTING_AS}</td>
                                        <td>{a.ACTION}</td>
                                        <td>{a.DETAIL}</td>
                                    </tr>
                                ))}
                                {activity.rows?.length === 0 && (
                                    <tr><td colSpan={5} className="text-center text-muted">No activity yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Index;
